import type { FabbleCardType } from "@fabkit/apps/fabble/types";
import { COLUMNS } from "@fabkit/apps/fabble/types";

export const FABBLE_DATA_URL = import.meta.env.DEV
	? "/fabble-sample-data.json"
	: "https://fabkit.github.io/fabble-data/v1/dataset.json";
// ^ THE swap point: dev uses the hand-authored fixture; production fetches
// the real feed generated by github.com/FABKIT/fabble-admin.

/** Schedule-driven daily modes — the only ones dataset.schedule[mode] is indexed by. */
export const MODES = ["standard", "chaos"] as const;
export type FabbleMode = (typeof MODES)[number];
export const MAX_GUESSES: Record<FabbleMode, number> = {
	standard: 8,
	chaos: 12,
};

/** Endless has no schedule entry and no guess cap — kept out of `FabbleMode`/`MODES` on
    purpose so `dataset.schedule[mode]` lookups can't silently accept it. */
export const ENDLESS_MODE = "endless" as const;
export type AnyFabbleMode = FabbleMode | typeof ENDLESS_MODE;
/** Display-only union of all three modes, for UI that iterates every mode (mode-select
    cards, end-screen mode-switch buttons). Never used to index dataset.schedule. */
export const ALL_MODES = [...MODES, ENDLESS_MODE] as const;

/** Single source of truth for mode -> route path, since ModeSelect and ModeSwitchButtons
    both need it and a third mode makes a ternary chain error-prone. */
export const MODE_ROUTES: Record<AnyFabbleMode, string> = {
	standard: "/fabble/standard",
	chaos: "/fabble/chaos",
	endless: "/fabble/endless",
};
export const HINT_UNLOCK_GUESSES = [3, 5] as const;
export const ELIGIBLE_TYPES: FabbleCardType[] = [
	"hero",
	"weapon",
	"equipment",
	"action",
	"attack_action",
	"attack_reaction",
	"defense_reaction",
	"instant",
	"ally",
]; // display order of the chips row (spec §4.3); labels via fabble i18n keys
export const MAX_SEARCH_RESULTS = 10;
export const USERNAME_MAX_LENGTH = 20;
export const SHARE_LINK = "https://fabkit.io/fabble";

/** Endless's "Next puzzle" crossfade — how long the outgoing puzzle fades out before the
    next one is picked and mounted. Must equal the fabble-fade-out duration in fabble.css. */
export const ENDLESS_TRANSITION_MS = 200;

/** Tile reveal timing — single source of truth shared by FeedbackBlock (per-tile
    animationDelay) and EndPanel (wait before fading in). Derived, never hardcoded:
    stays correct if COLUMNS or the stagger/flip durations ever change. */
export const TILE_STAGGER_MS = 80;
export const TILE_FLIP_MS = 500; // must equal the fabble-flip duration in fabble.css
export const REVEAL_TOTAL_MS =
	(COLUMNS.length - 1) * TILE_STAGGER_MS + TILE_FLIP_MS;
